import { useCallback, useEffect, useState } from 'react';

import { getScenario } from '../../../shared/api/scenarioApi';

import { useScenario } from '../../../shared/contexts/ScenarioContext';

import useScenarioMode from '../../../shared/hooks/useScenarioMode';

import { getPortsByCountry } from '../../master-data';

import { getVoyageGroup } from '../services/VoyageRepository';

import { deriveGroupParams } from '../views/sales/modals/groupEditHelpers';

import type {
  ShipGroup,
  VoyageRow,
} from '../views/sales/modals/groupEditTypes';

export interface ScenarioDeltaMaps {
  deltaJsonMap: Record<string, Record<string, unknown>>;
  voyageChangeTypeMap: Record<string, string>;
  shipmentDeltaByShipmentId: Record<string, Record<string, unknown>>;
  shipmentChangeTypeById: Record<string, string>;
  /** Case-3: CREATED shipments for wholly-new destination blocks.
   *  Keyed by scenarioVoyageDeltaId (the CREATED voyage delta's id). */
  createdShipmentsByVoyageDeltaId: Record<
    string,
    Array<Record<string, unknown>>
  >;
  /** Case-2: CREATED shipments added to an existing voyage.
   *  Keyed by the real voyageId stored inside deltaJson.voyageId. */
  createdShipmentsByVoyageId: Record<string, Array<Record<string, unknown>>>;
  createdVoyageDeltas: Array<{
    id: string;
    deltaJson: Record<string, unknown>;
  }>;
  voyageDeltaIdToVoyageId: Record<string, string>;
  voyageDeltaIdByVoyageId: Record<string, string>;
  /** All shipment delta IDs scoped to this group's voyages (for undo cleanup) */
  groupShipmentDeltaIds: string[];
}

export interface UseGroupEditScenarioDataResult extends ScenarioDeltaMaps {
  freshGroup: ShipGroup | null;
  baseGroup: ShipGroup | null;
  departurePorts: { label: string; value: string }[];
  isLoading: boolean;
}

interface Props {
  open: boolean;
  isCreateMode: boolean;
  shipGroup?: ShipGroup | null;
  voyageRow?: VoyageRow | null;
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info'
  ) => void;
  onClose: () => void;
}

const useGroupEditScenarioData = ({
  open,
  isCreateMode,
  shipGroup,
  voyageRow,
  showSnackbar,
  onClose,
}: Props): UseGroupEditScenarioDataResult => {
  const { isScenarioActive } = useScenarioMode();
  const { activeScenario } = useScenario();

  const [isLoading, setIsLoading] = useState(false);
  const [freshGroup, setFreshGroup] = useState<ShipGroup | null>(null);
  const [baseGroup, setBaseGroup] = useState<ShipGroup | null>(null);
  const [departurePorts, setDeparturePorts] = useState<
    { label: string; value: string }[]
  >([]);

  // All scenario maps updated atomically to avoid race with freshGroup
  const [deltaJsonMap, setDeltaJsonMap] = useState<
    Record<string, Record<string, unknown>>
  >({});
  // voyageId → changeType (so CANCELLED deltas can restore _cancelled:true on re-open)
  const [voyageChangeTypeMap, setVoyageChangeTypeMap] = useState<
    Record<string, string>
  >({});
  // shipmentId → deltaJson (keyed by original shipmentId, not scenarioVoyageDeltaId which may be null)
  const [shipmentDeltaByShipmentId, setShipmentDeltaByShipmentId] = useState<
    Record<string, Record<string, unknown>>
  >({});
  // shipmentId → changeType (for CANCELLED shipment highlighting)
  const [shipmentChangeTypeById, setShipmentChangeTypeById] = useState<
    Record<string, string>
  >({});
  // scenarioVoyageDeltaId → CREATED shipment deltas (case 3: new destination blocks)
  const [createdShipmentsByVoyageDeltaId, setCreatedShipmentsByVoyageDeltaId] =
    useState<Record<string, Array<Record<string, unknown>>>>({});
  // voyageId → CREATED shipment deltas (case 2: new markets on existing voyages)
  const [createdShipmentsByVoyageId, setCreatedShipmentsByVoyageId] = useState<
    Record<string, Array<Record<string, unknown>>>
  >({});
  // CREATED voyage deltas (voyageId === null)
  const [createdVoyageDeltas, setCreatedVoyageDeltas] = useState<
    Array<{ id: string; deltaJson: Record<string, unknown> }>
  >([]);
  // voyageDeltaId → voyageId (for matching CREATED shipments to existing voyage destinations)
  const [voyageDeltaIdToVoyageId, setVoyageDeltaIdToVoyageId] = useState<
    Record<string, string>
  >({});
  // voyageId → deltaId — scoped to this group's voyages only
  const [voyageDeltaIdByVoyageId, setVoyageDeltaIdByVoyageId] = useState<
    Record<string, string>
  >({});
  // All shipment delta IDs scoped to this group's voyages (for undo cleanup)
  const [groupShipmentDeltaIds, setGroupShipmentDeltaIds] = useState<string[]>(
    []
  );

  const resetAll = useCallback(() => {
    setFreshGroup(null);
    setBaseGroup(null);
    setDeparturePorts([]);
    setIsLoading(false);
    setDeltaJsonMap({});
    setVoyageChangeTypeMap({});
    setShipmentDeltaByShipmentId({});
    setShipmentChangeTypeById({});
    setCreatedShipmentsByVoyageDeltaId({});
    setCreatedShipmentsByVoyageId({});
    setCreatedVoyageDeltas([]);
    setVoyageDeltaIdToVoyageId({});
    setVoyageDeltaIdByVoyageId({});
    setGroupShipmentDeltaIds([]);
  }, []);

  // Fetch fresh group data + departure ports; stale-fetch guard prevents
  // race conditions when the modal is re-opened or props change rapidly.
  useEffect(() => {
    let cancelled = false;

    if (open && !isCreateMode && (shipGroup || voyageRow)) {
      const params = deriveGroupParams(shipGroup, voyageRow);

      if (!params) {
        showSnackbar('Unable to determine voyage group parameters');
        onClose();
        return;
      }

      setIsLoading(true);

      Promise.all([
        getVoyageGroup({
          imo: params.imo,
          departurePort: params.departurePort,
          portOperator: params.portOperator,
          etdDate: params.etdDate,
          ...(isScenarioActive && activeScenario
            ? { scenarioId: activeScenario.id }
            : {}),
        }).catch(() => null),
        params.depCountry
          ? getPortsByCountry(params.depCountry).then((list: any[]) =>
              list.map((p: any) => ({
                label: `${p.unLocode} ${p.name}`,
                value: p.unLocode,
              }))
            )
          : Promise.resolve([]),
        isScenarioActive && activeScenario
          ? getScenario(activeScenario.id).catch(() => null)
          : Promise.resolve(null),
        // Fetch the base (non-scenario) group to recover CANCELLED destinations
        // that the backend's scenario-overlaid response excludes.
        isScenarioActive && activeScenario
          ? getVoyageGroup({
              imo: params.imo,
              departurePort: params.departurePort,
              portOperator: params.portOperator,
              etdDate: params.etdDate,
            })
              .then((data: any) =>
                data && Object.keys(data).length > 0 ? data : null
              )
              .catch(() => null)
          : Promise.resolve(null),
      ])
        .then(([freshGroupData, ports, scenarioData, baseGroupData]) => {
          if (cancelled) return;

          // When a scenario delta changed a group key field (e.g. ETD), the
          // scenario-overlaid getVoyageGroup call may return null because the
          // backend's post-overlay filter no longer matches the query params.
          // Fall back to the base group — the overlay hook will apply the
          // delta maps on top of it to produce correct editData.
          if (!freshGroupData && !baseGroupData) {
            showSnackbar('No voyages found for the given group parameters');
            onClose();
            return;
          }
          const effectiveFreshGroup = freshGroupData ?? baseGroupData;

          // Compute maps before any setState so React batches everything together
          const jsonMap: Record<string, Record<string, unknown>> = {};
          const changeTypeMap: Record<string, string> = {};
          const sByShipmentId: Record<string, Record<string, unknown>> = {};
          const sByChangeType: Record<string, string> = {};
          const createdShipmentsByVoyageDeltaIdLocal: Record<
            string,
            Array<Record<string, unknown>>
          > = {};
          const createdShipmentsByVoyageIdLocal: Record<
            string,
            Array<Record<string, unknown>>
          > = {};
          const createdVoyages: Array<{
            id: string;
            deltaJson: Record<string, unknown>;
          }> = [];
          const dvIdToVoyageId: Record<string, string> = {};
          const deltaIdByVoyageId: Record<string, string> = {};
          const groupShipmentDeltaIdsLocal: string[] = [];

          // Only include deltas for voyages that belong to this group.
          // Use BOTH effectiveFreshGroup (scenario-overlaid, or base fallback)
          // AND baseGroupData (raw DB) so that CANCELLED voyages — which the
          // backend may exclude from the overlaid response — are still recognised.
          const freshDestinations = effectiveFreshGroup
            ? Object.values((effectiveFreshGroup as ShipGroup).destinations)
            : [];
          const baseDestinations = baseGroupData
            ? Object.values((baseGroupData as ShipGroup).destinations)
            : [];
          const allGroupDestinations = [
            ...freshDestinations,
            ...baseDestinations,
          ];
          const groupVoyageIds = new Set<string>(
            allGroupDestinations.flatMap(
              (dest) =>
                dest.voyages
                  .map((v) => (v.id != null ? String(v.id) : null))
                  .filter(Boolean) as string[]
            )
          );
          // Real shipment IDs in this group — used to skip CREATED shipment deltas
          // whose own id collides with a real shipment (backend id-reuse pattern).
          const groupShipmentIds = new Set<string>(
            allGroupDestinations.flatMap((dest) =>
              dest.voyages.flatMap(
                (v) =>
                  (v.shipments ?? [])
                    .map((s: any) => (s.id != null ? String(s.id) : null))
                    .filter(Boolean) as string[]
              )
            )
          );

          if (scenarioData) {
            (scenarioData.voyageDeltas ?? []).forEach((d) => {
              if (d.voyageId) {
                if (d.deltaJson)
                  jsonMap[d.voyageId] = d.deltaJson as Record<string, unknown>;
                changeTypeMap[d.voyageId] = d.changeType;
                dvIdToVoyageId[d.id] = d.voyageId;
                if (groupVoyageIds.has(d.voyageId))
                  deltaIdByVoyageId[d.voyageId] = d.id;
              } else if (d.changeType === 'CREATED' && d.deltaJson) {
                // Always register the delta's own id in dvIdToVoyageId so that
                // shipment deltas linked via scenarioVoyageDeltaId can be resolved
                // and filtered (the groupVoyageIds check will decide whether to inject).
                dvIdToVoyageId[d.id] = d.id;
                if (groupVoyageIds.has(d.id)) {
                  // The delta's own id matches a real voyage — this CREATED delta
                  // was previously saved against an existing voyage. Its shipments
                  // (keyed by scenarioVoyageDeltaId = d.id) should merge into the
                  // existing destination, not form a new synthetic block.
                  // Populate deltaJsonMap/changeTypeMap so the overlay highlights
                  // modified fields on the existing destination block.
                  if (d.deltaJson)
                    jsonMap[d.id] = d.deltaJson as Record<string, unknown>;
                  changeTypeMap[d.id] = d.changeType;
                } else {
                  // Only include this CREATED voyage delta if it belongs to the
                  // current group — match on IMO, departure port, portOperator and
                  // ETD date so deltas from other groups are not injected here.
                  const dj = d.deltaJson as Record<string, unknown>;
                  const deltaImo = (dj.ship as any)?.imo;
                  const deltaDep = (dj.departurePort as any)?.unLocode;
                  const deltaPo = (dj.portOperator as any)?.supplierNo;
                  const deltaEtd = (dj.actualETD ?? dj.initialETD) as
                    | string
                    | undefined;
                  const deltaEtdDate = deltaEtd
                    ? deltaEtd.slice(0, 10)
                    : undefined;
                  const groupEtdDate = params.etdDate
                    ? params.etdDate.slice(0, 10)
                    : undefined;

                  const imoMatch =
                    !deltaImo || String(deltaImo) === String(params.imo);
                  const depMatch =
                    !deltaDep || deltaDep === params.departurePort;
                  const poMatch =
                    !deltaPo || String(deltaPo) === String(params.portOperator);
                  const etdMatch =
                    !deltaEtdDate ||
                    !groupEtdDate ||
                    deltaEtdDate === groupEtdDate;

                  if (imoMatch && depMatch && poMatch && etdMatch) {
                    createdVoyages.push({
                      id: d.id,
                      deltaJson: dj,
                    });
                  }
                }
              }
            });

            (scenarioData.shipmentDeltas ?? []).forEach((sd) => {
              if (sd.shipmentId) {
                // Track if this shipment belongs to the current group
                if (groupShipmentIds.has(sd.shipmentId)) {
                  groupShipmentDeltaIdsLocal.push(sd.id);
                }
                if (sd.deltaJson)
                  sByShipmentId[sd.shipmentId] = sd.deltaJson as Record<
                    string,
                    unknown
                  >;
                sByChangeType[sd.shipmentId] = sd.changeType;
              } else if (sd.changeType === 'CREATED' && sd.deltaJson) {
                // Skip if the delta's own id collides with a real shipment — the
                // real shipment row already represents this data in the form.
                if (groupShipmentIds.has(sd.id)) return;
                if (sd.scenarioVoyageDeltaId) {
                  const dvId = sd.scenarioVoyageDeltaId;
                  const resolvedVoyageId = dvIdToVoyageId[dvId];
                  if (
                    resolvedVoyageId &&
                    groupVoyageIds.has(resolvedVoyageId)
                  ) {
                    // The voyage delta's own id maps to a real voyage in THIS group.
                    // Merge the shipment into the existing destination.
                    groupShipmentDeltaIdsLocal.push(sd.id);
                    if (!createdShipmentsByVoyageIdLocal[resolvedVoyageId])
                      createdShipmentsByVoyageIdLocal[resolvedVoyageId] = [];
                    createdShipmentsByVoyageIdLocal[resolvedVoyageId].push(
                      sd.deltaJson as Record<string, unknown>
                    );
                  } else if (!resolvedVoyageId) {
                    // Case 3: shipment belongs to a wholly-new destination block
                    groupShipmentDeltaIdsLocal.push(sd.id);
                    if (!createdShipmentsByVoyageDeltaIdLocal[dvId])
                      createdShipmentsByVoyageDeltaIdLocal[dvId] = [];
                    createdShipmentsByVoyageDeltaIdLocal[dvId].push(
                      sd.deltaJson as Record<string, unknown>
                    );
                  }
                } else {
                  // Case 2: new market added to an existing voyage (voyageId in deltaJson)
                  const vid = (sd.deltaJson as Record<string, unknown>)
                    .voyageId as string | undefined;
                  // Only inject if this voyageId belongs to the current group
                  if (vid && groupVoyageIds.has(vid)) {
                    groupShipmentDeltaIdsLocal.push(sd.id);
                    if (!createdShipmentsByVoyageIdLocal[vid])
                      createdShipmentsByVoyageIdLocal[vid] = [];
                    createdShipmentsByVoyageIdLocal[vid].push(
                      sd.deltaJson as Record<string, unknown>
                    );
                  }
                }
              }
            });
          }

          setFreshGroup(effectiveFreshGroup as ShipGroup | null);
          setBaseGroup(
            (baseGroupData ?? effectiveFreshGroup) as ShipGroup | null
          );
          setDeparturePorts(ports);
          setDeltaJsonMap(jsonMap);
          setVoyageChangeTypeMap(changeTypeMap);
          setShipmentDeltaByShipmentId(sByShipmentId);
          setShipmentChangeTypeById(sByChangeType);
          setCreatedShipmentsByVoyageDeltaId(
            createdShipmentsByVoyageDeltaIdLocal
          );
          setCreatedShipmentsByVoyageId(createdShipmentsByVoyageIdLocal);
          setCreatedVoyageDeltas(createdVoyages);
          setVoyageDeltaIdToVoyageId(dvIdToVoyageId);
          setVoyageDeltaIdByVoyageId(deltaIdByVoyageId);
          setGroupShipmentDeltaIds(groupShipmentDeltaIdsLocal);
        })
        .catch((error: any) => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.error(
            '[GroupEditModal] Failed to fetch voyage group:',
            error
          );
          showSnackbar(error?.message || 'Error loading voyage group');
          onClose();
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    }

    if (!open) {
      resetAll();
    }

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isCreateMode,
    shipGroup,
    voyageRow,
    showSnackbar,
    onClose,
    isScenarioActive,
    activeScenario,
  ]);

  return {
    freshGroup,
    baseGroup,
    departurePorts,
    isLoading,
    deltaJsonMap,
    voyageChangeTypeMap,
    shipmentDeltaByShipmentId,
    shipmentChangeTypeById,
    createdShipmentsByVoyageDeltaId,
    createdShipmentsByVoyageId,
    createdVoyageDeltas,
    voyageDeltaIdToVoyageId,
    voyageDeltaIdByVoyageId,
    groupShipmentDeltaIds,
  };
};

export default useGroupEditScenarioData;
